import { client } from '@/client';
import {
  CampaignMasterTemplate,
  CampaignMasterTemplatesListParams,
  CampaignMasterTemplatesListResponse,
  CreateCampaignMasterTemplateRequest,
  PhishingTranslationStatusResponse,
  TranslatePhishingTemplateRequest,
  TranslatePhishingTemplateResponse,
  UpdateCampaignMasterTemplateRequest,
} from '@/client/campaigns';
import { GenericForbiddenApiError } from '@/hooks/query';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const MASTER_TEMPLATES_QUERY_KEY = 'campaignMasterTemplates';

export const useCampaignMasterTemplates = (
  params?: CampaignMasterTemplatesListParams,
  options: UseQueryOptions = {},
): UseQueryResult<CampaignMasterTemplatesListResponse> & {
  masterTemplates: CampaignMasterTemplatesListResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [MASTER_TEMPLATES_QUERY_KEY, params],
    () => client.masterTemplates.getMasterTemplates(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    masterTemplates: data,
    ...(rest as UseQueryResult<CampaignMasterTemplatesListResponse>),
  };
};

export const useCampaignMasterTemplate = (
  id: string,
  options: UseQueryOptions = {},
): UseQueryResult<CampaignMasterTemplate> & {
  masterTemplate: CampaignMasterTemplate | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [MASTER_TEMPLATES_QUERY_KEY, id],
    () => client.masterTemplates.getMasterTemplate(id),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!id,
      ...(restOptions as any),
    },
  );

  return {
    masterTemplate: data,
    ...(rest as UseQueryResult<CampaignMasterTemplate>),
  };
};

export const useCreateCampaignMasterTemplate = (): {
  create: (
    payload: CreateCampaignMasterTemplateRequest,
  ) => Promise<CampaignMasterTemplate>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    CampaignMasterTemplate,
    GenericForbiddenApiError,
    CreateCampaignMasterTemplateRequest
  >(
    'createCampaignMasterTemplate',
    (payload) => client.masterTemplates.createMasterTemplate(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
      },
    },
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateCampaignMasterTemplate = (): {
  update: (payload: {
    id: string;
    request: UpdateCampaignMasterTemplateRequest;
  }) => Promise<CampaignMasterTemplate>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    CampaignMasterTemplate,
    GenericForbiddenApiError,
    { id: string; request: UpdateCampaignMasterTemplateRequest }
  >(
    'updateCampaignMasterTemplate',
    (payload) =>
      client.masterTemplates.updateMasterTemplate(payload.id, payload.request),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY, id]);
      },
    },
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteCampaignMasterTemplate = (): {
  remove: (id: string) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    string
  >(
    'deleteCampaignMasterTemplate',
    (id) => client.masterTemplates.deleteMasterTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
      },
    },
  );

  return { remove: mutateAsync, ...(rest as any) };
};

export const useDisassociateCampaignTemplate = (): {
  disassociate: (payload: {
    masterTemplateId: string;
    templateId: string;
  }) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    { masterTemplateId: string; templateId: string }
  >(
    'disassociateCampaignTemplate',
    ({ masterTemplateId, templateId }) =>
      client.masterTemplates.disassociateTemplate(masterTemplateId, templateId),
    {
      onSuccess: (_, { masterTemplateId }) => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
        queryClient.invalidateQueries([
          MASTER_TEMPLATES_QUERY_KEY,
          masterTemplateId,
        ]);
      },
    },
  );

  return { disassociate: mutateAsync, ...(rest as any) };
};

export const useSetPrimaryTemplate = (): {
  setPrimary: (payload: {
    masterTemplateId: string;
    templateId: string;
  }) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    { masterTemplateId: string; templateId: string }
  >(
    'setPrimaryTemplate',
    ({ masterTemplateId, templateId }) =>
      client.masterTemplates.setPrimaryTemplate(masterTemplateId, templateId),
    {
      onSuccess: (_, { masterTemplateId }) => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
        queryClient.invalidateQueries([
          MASTER_TEMPLATES_QUERY_KEY,
          masterTemplateId,
        ]);
      },
    },
  );

  return { setPrimary: mutateAsync, ...(rest as any) };
};

export const useTranslatePhishingTemplate = (): {
  translate: (payload: {
    masterTemplateId: string;
    request: TranslatePhishingTemplateRequest;
  }) => Promise<TranslatePhishingTemplateResponse>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    TranslatePhishingTemplateResponse,
    GenericForbiddenApiError,
    { masterTemplateId: string; request: TranslatePhishingTemplateRequest }
  >(
    'translatePhishingTemplate',
    ({ masterTemplateId, request }) =>
      client.masterTemplates.translatePhishingTemplate(
        masterTemplateId,
        request,
      ),
    {
      onSuccess: (_, { masterTemplateId }) => {
        queryClient.invalidateQueries([MASTER_TEMPLATES_QUERY_KEY]);
        queryClient.invalidateQueries([
          MASTER_TEMPLATES_QUERY_KEY,
          masterTemplateId,
        ]);
      },
    },
  );

  return { translate: mutateAsync, ...(rest as any) };
};

const translationStatusQueryKey = 'phishingTranslationStatus';

export const usePhishingTranslationStatus = (
  masterTemplateId: string,
  options: UseQueryOptions = {},
): UseQueryResult<PhishingTranslationStatusResponse> & {
  translationStatus: PhishingTranslationStatusResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [translationStatusQueryKey, masterTemplateId],
    () => client.masterTemplates.getPhishingTranslationStatus(masterTemplateId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!masterTemplateId,
      ...(restOptions as any),
    },
  );

  return {
    translationStatus: data,
    ...(rest as UseQueryResult<PhishingTranslationStatusResponse>),
  };
};
